/**
 * `<AttachmentVideoPlayer>` — the one `<video>` element + MediaError
 * recovery machine every attachment-backed video embed draws.
 *
 * Extracted from the wiki `VideoEmbedView` so the wiki doc body and the
 * card / comment surfaces cannot drift on the subtle half of the
 * feature. Two distinct failures hide behind one `onError`, and getting
 * either wrong is silent:
 *
 *   - `MEDIA_ERR_NETWORK` (code 2) is almost always a signed URL that
 *     expired mid-playback. A fresh presign fixes it, but the retry has
 *     to be BOUNDED: when the re-presign succeeds while the bytes still
 *     fail (an S3 5xx, a flaky link), the naive shape cycles
 *     error → refresh → remount → error at one presign per iteration for
 *     as long as the tab stays open.
 *   - `MEDIA_ERR_DECODE` / `MEDIA_ERR_SRC_NOT_SUPPORTED` (codes 3 + 4,
 *     i.e. an iPhone HEVC `.mov` in Chrome) must NEVER refetch — no
 *     number of re-presigns changes a codec, so a retry there is a
 *     spinner the reader can never escape. The bytes are intact, so the
 *     player is swapped for a card that hands them over instead.
 *
 * The remount key is what makes recovery actually work: without it the
 * same dead `src` stays attached and the browser never retries.
 *
 * Chrome is the caller's: `className` styles the unplayable panel and
 * `videoClassName` / `videoStyle` the element, so the wiki's 720px
 * bordered card and the card surface's column-width frame share one
 * implementation without either dictating the other's look.
 *
 * NO `crossOrigin` on the `<video>` — the page only DISPLAYS the bytes;
 * the download rides a separately signed URL, and flagging the element
 * would break rendering for any origin S3 doesn't answer with ACAO.
 *
 * Loading is deliberately NOT owned here. Each surface's skeleton has
 * its own chrome, and both callers already check `unplayable` ahead of
 * their loading branch so a refresh still in flight can't flash a
 * skeleton over a decision that is already made.
 */

import { Download, FileVideo } from 'lucide-react'
import { type CSSProperties, useCallback, useState } from 'react'

import { useToast } from '@/components/ToastProvider'
import { downloadAttachment } from '@/lib/download-file'

// MediaError codes (https://html.spec.whatwg.org/#mediaerror):
// 1 ABORTED, 2 NETWORK, 3 DECODE, 4 SRC_NOT_SUPPORTED.
const MEDIA_ERR_NETWORK = 2
const MEDIA_ERR_DECODE = 3
const MEDIA_ERR_SRC_NOT_SUPPORTED = 4

/** Network-error re-presign attempts before falling through to the card. */
const MAX_NETWORK_RETRIES = 2

/**
 * Copy is passed in rather than read from a catalog here: the wiki keeps
 * its `wiki.video.*` strings and the card surfaces use
 * `uploads.embed.video*`, so a shared component that reached for one
 * namespace would put the other surface's strings in the wrong file.
 */
export interface AttachmentVideoPlayerLabels {
  /** Accessible name for the `<video>` element. */
  ariaLabel: string
  /** Body text for browsers that render no player at all. */
  fallback: string
  /** Heading of the unplayable card. */
  unplayableTitle: string
  /** Explanation under that heading. */
  unplayableBody: string
  /** Label of the unplayable card's download button. */
  download: string
  /** Toast shown when the download itself fails. */
  downloadFailed: string
}

export interface AttachmentVideoPlayerProps {
  /** Attachment id — also the identity this component's state is keyed on. */
  fileId: string
  /**
   * Resolved presigned GET URL. Callers render their own skeleton until
   * it exists; rendering this component without one draws nothing (the
   * unplayable card is the exception — it hands over the bytes through a
   * separately signed download and needs no playback URL).
   */
  url: string | undefined
  /** Mints a fresh presigned URL; called only on a recoverable network error. */
  refresh: () => Promise<void>
  labels: AttachmentVideoPlayerLabels
  /** Classes for the unplayable panel's container. */
  className?: string
  /** Classes for the `<video>` element. */
  videoClassName?: string
  /** Inline styles for the `<video>` element (the pre-metadata box, object-fit). */
  videoStyle?: CSSProperties
  /** Fires once the browser knows the source's intrinsic dimensions. */
  onLoadedMetadata?: () => void
  /**
   * Called when this file's bytes have been rejected by the browser, so a
   * caller can mirror the decision onto its own wrapper (`data-state`) and
   * order its own branches around it. Fired from the error handler only —
   * the caller derives the RESET from `fileId` rather than being told,
   * which keeps this out of render.
   */
  onUnplayable?: () => void
}

export function AttachmentVideoPlayer({
  fileId,
  url,
  refresh,
  labels,
  className,
  videoClassName,
  videoStyle,
  onLoadedMetadata,
  onUnplayable,
}: AttachmentVideoPlayerProps) {
  const toast = useToast()
  // Bumped on every recoverable `<video onError>` so `<video src>`
  // re-attaches against the fresh URL after a `refresh()` round-trip.
  // Without the remount the same expired src stays attached and the
  // browser doesn't retry.
  const [remountKey, setRemountKey] = useState(0)
  // Set when the browser reports it cannot decode the container / codec.
  // Swaps the dead player for a card that hands over the bytes instead.
  const [unplayable, setUnplayable] = useState(false)
  const [networkRetries, setNetworkRetries] = useState(0)
  const [downloading, setDownloading] = useState(false)

  // Reset the transient state when the component points at a different
  // file — the upload flow writes `fileId` onto the SAME node, so the
  // instance is reused and a failed decode would otherwise leave its card
  // standing over a file that plays fine. Adjusted during render rather
  // than in an effect: that is React's documented pattern for derived
  // state, it avoids the one frame the effect version paints with the
  // previous file's state, and Biome rejects an effect whose body only
  // calls setState.
  const [renderedFileId, setRenderedFileId] = useState(fileId)
  if (renderedFileId !== fileId) {
    setRenderedFileId(fileId)
    setUnplayable(false)
    setRemountKey(0)
    setNetworkRetries(0)
  }

  const onVideoError = useCallback(
    async (e: React.SyntheticEvent<HTMLVideoElement>) => {
      const code = e.currentTarget.error?.code
      // A network error is almost always signed-URL expiry, which a fresh
      // presign fixes — but only a bounded number of times.
      if (code === MEDIA_ERR_NETWORK) {
        if (networkRetries >= MAX_NETWORK_RETRIES) {
          setUnplayable(true)
          onUnplayable?.()
          return
        }
        setNetworkRetries((n) => n + 1)
        await refresh()
        setRemountKey((k) => k + 1)
        return
      }
      // A codec or container this browser can't decode — an iPhone HEVC
      // `.mov` in Chrome is the common case. The bytes are fine and no
      // number of re-presigns changes that, so never refetch here.
      if (code === MEDIA_ERR_DECODE || code === MEDIA_ERR_SRC_NOT_SUPPORTED) {
        setUnplayable(true)
        onUnplayable?.()
      }
    },
    [refresh, networkRetries, onUnplayable],
  )

  const handleDownload = useCallback(async () => {
    if (downloading) return
    setDownloading(true)
    try {
      // Routed through the attachment id, never a blob fetch: S3 supplies
      // the original filename via `Content-Disposition`, and a cross-origin
      // fetch from localhost fails the uploads bucket's CORS allowlist.
      await downloadAttachment(fileId)
    } catch {
      toast.error(labels.downloadFailed)
    } finally {
      setDownloading(false)
    }
  }, [fileId, downloading, labels.downloadFailed, toast])

  if (unplayable) {
    return (
      <div className={className}>
        <FileVideo aria-hidden="true" size={32} className="text-fg-muted" />
        <span className="text-sm font-semibold text-fg">{labels.unplayableTitle}</span>
        <span className="text-xs text-fg-muted">{labels.unplayableBody}</span>
        <button
          type="button"
          onClick={handleDownload}
          // ProseMirror's selection-on-mousedown for an atomic node
          // otherwise swallows the click intent.
          onMouseDown={(e) => e.stopPropagation()}
          disabled={downloading}
          className="mt-1 inline-flex items-center gap-1 rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs font-medium text-fg hover:bg-bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-60"
        >
          <Download aria-hidden="true" size={12} />
          {labels.download}
        </button>
      </div>
    )
  }

  if (!url) return null

  return (
    // biome-ignore lint/a11y/useMediaCaption: no WebVTT track is authored — the node's `caption` attr is descriptive text rendered below the player, not a caption track. Same exemption the chat timeline carries.
    <video
      key={remountKey}
      src={url}
      controls
      preload="metadata"
      playsInline
      draggable={false}
      onError={onVideoError}
      onLoadedMetadata={onLoadedMetadata}
      aria-label={labels.ariaLabel}
      className={videoClassName}
      style={videoStyle}
    >
      {labels.fallback}
    </video>
  )
}

AttachmentVideoPlayer.displayName = 'AttachmentVideoPlayer'
